import { useMemo } from 'react';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineChevronLeftIcon } from '@tempmee/react-icons';
import { Column } from '~/commonComponents';
import { TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';
import { EducationUtils } from '~/utils/EducationUtils';
import { UserProfession } from '~/enums/UserProfession';

interface IViewSkillsScreenProps {
  fullName: string;
  profession: string;
  professionCode: string;
  education: TEducationMap;
  onBack: () => void;
}

const isDnDa = (code: string) => [UserProfession.DN, UserProfession.DA].includes(code);

const isRdhFd = (code: string) => [UserProfession.RDH, UserProfession.FD].includes(code);

export const ViewSkillsScreen = ({
  fullName,
  profession,
  professionCode,
  education,
  onBack,
}: IViewSkillsScreenProps) => {
  // Try exact professionCode first, then fall back to first available entry
  const resolvedCode = education[professionCode]
    ? professionCode
    : (Object.keys(education)[0] ?? professionCode);
  const educationEntry = education[resolvedCode];
  const specialtiesAndProcedures = useMemo(
    () => educationEntry?.specialtiesAndProcedures ?? {},
    [educationEntry],
  );

  // DN/DA: specialty keys map to procedure arrays
  const dnDaSpecialties = useMemo(() => {
    if (!isDnDa(resolvedCode)) return [];
    return Object.entries(specialtiesAndProcedures);
  }, [resolvedCode, specialtiesAndProcedures]);

  // RDH/FD: procedures are top-level keys prefixed with "procedure_"
  const rdhFdProcedures = useMemo(() => {
    if (!isRdhFd(resolvedCode)) return [];
    return EducationUtils.getProceduresOfProfession({
      specialtiesAndProcedures,
      profession: resolvedCode,
      specialties: [],
    }) as string[];
  }, [resolvedCode, specialtiesAndProcedures]);

  return (
    <Column flex={1} position="relative">
      <Button
        variant="ghost"
        size="inline"
        onClick={onBack}
        LeadingIcon={<OutlineChevronLeftIcon />}
        style={{ position: 'absolute', left: 24, top: 24, zIndex: 1 }}
      >
        Back
      </Button>

      <Column marginBottom={24} marginTop={54}>
        <Typography as="span" bold size="body_large" textAlign="center">
          Professional Skills
        </Typography>
      </Column>

      <Column paddingHorizontal={48} marginBottom={16}>
        <Typography as="span" size="heading_4">
          <Typography as="span" bold size="heading_4" color={colors.neutral[500]}>
            {fullName}
          </Typography>
          <Typography as="span" size="body_large" color={colors.neutral[500]}>
            {` - ${profession}`}
          </Typography>
        </Typography>
      </Column>

      <Column
        flex={1}
        flexBasis={0}
        overflowY="auto"
        paddingHorizontal={48}
        style={{ scrollbarWidth: 'thin' }}
      >
        {isDnDa(resolvedCode) &&
          dnDaSpecialties.map(([specialtyKey, procedures]) => (
            <Column key={specialtyKey} marginBottom={16}>
              <Typography as="span" bold size="body_large" color={colors.primary[500]}>
                {EducationUtils.removeSpecialtyPrefix(specialtyKey)}
              </Typography>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginTop: 8,
                }}
              >
                {procedures.map((proc: string) => (
                  <Typography as="span" key={proc} size="body_large" color={colors.neutral[600]}>
                    {`\u2022  ${EducationUtils.removeProcedurePrefix(proc)}`}
                  </Typography>
                ))}
              </div>
            </Column>
          ))}

        {isRdhFd(resolvedCode) && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {rdhFdProcedures.map((name: string) => (
              <Typography as="span" key={name} size="body_large" color={colors.neutral[600]}>
                {`\u2022  ${EducationUtils.removeProcedurePrefix(name)}`}
              </Typography>
            ))}
          </div>
        )}
      </Column>
    </Column>
  );
};
